import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { TripCard } from '../components/TripCard';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Users, 
  Shuffle, 
  User, 
  MapPin, 
  Shield, 
  Compass, 
  Sparkles,
  Star,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1539768942893-daf53e448371?w=1920&q=80";

export default function HomePage() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [email, setEmail] = useState('');
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Seed data first
      await axios.post(`${API}/seed`);
      
      const [tripsRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/trips/featured`),
        axios.get(`${API}/testimonials`)
      ]);
      setTrips(tripsRes.data);
      setTestimonials(testimonialsRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success('Successfully subscribed!');
      setEmail('');
    } catch (err) {
      toast.error('Failed to subscribe');
    }
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const features = [
    {
      icon: Shield,
      title: "No Stress. No Guess.",
      description: "We line up the stays, local transportation, activities, and insider spots — you just show up and play."
    },
    {
      icon: Compass,
      title: "For Travelers, Not Tourists",
      description: "We design journeys for curious travelers who want to dive in, not just skim the surface."
    },
    {
      icon: Sparkles,
      title: "Beyond the Basics",
      description: "Yes, you'll see the icons—but we also unlock hidden gems and authentic experiences many travelers miss."
    },
    {
      icon: Users,
      title: "Guides Who Get It",
      description: "Not cookie-cutter chaperones. Real hosts with real connections—so you feel plugged in, not herded around."
    }
  ];

  const travelTypes = [
    {
      icon: Users,
      title: "Join the Crew",
      description: "Our scheduled group trips run all year—perfect if you want the adventure without the planning. See upcoming departure dates and book your spot.",
      cta: "View Group Tours",
      link: "/group-adventures",
      image: "https://images.unsplash.com/photo-1758599669009-5a9002c09487?w=600"
    },
    {
      icon: User,
      title: "Go Solo",
      description: "Flying solo? Join a group as a soloist, take a private journey, or let us match you with another solo traveler for the double-occupancy rate.",
      cta: "Explore Solo Options",
      link: "/solo-adventures",
      image: "https://images.unsplash.com/photo-1528526938169-3e862c52e68a?w=600"
    },
    {
      icon: Shuffle,
      title: "Flip the Script",
      description: "Want it your way? Run any itinerary on your dates, as a private group, or create something entirely custom. We'll make it happen.",
      cta: "Customize Your Trip",
      link: "/customize",
      image: "https://images.unsplash.com/photo-1595690113904-04585db75b49?w=600"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/40 via-primary/60 to-primary/90" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center text-white pt-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              We Plan,<br />
              <span className="text-cyan-400">You Play!</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto mb-10">
              Whether you're looking to join a small group tour or set out on a solo adventure—we'll set the tone with curated itineraries to make your own.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => navigate('/trips')}
                className="bg-secondary hover:bg-secondary/90 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
                data-testid="hero-explore-btn"
              >
                Explore Adventures
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
              >
                Learn More
              </Button>
            </div>
          </motion.div>

          {/* Payment Plans Highlight */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-16 bg-white/10 backdrop-blur-md rounded-2xl p-6 max-w-md mx-auto"
          >
            <p className="text-sm text-white/70 mb-2">NEW: Flexible Payment Plans</p>
            <p className="text-2xl font-heading font-semibold">
              Pay in <span className="text-secondary">3, 6, or 12</span> monthly installments
            </p>
          </motion.div>
        </div>

      </section>

      {/* Travel Types Section */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-4xl font-bold text-primary mb-4">Travel With Us</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Choose your adventure style. We've got options for every type of traveler.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {travelTypes.map((type, idx) => (
              <motion.div
                key={type.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="relative h-80 rounded-2xl overflow-hidden mb-6">
                  <img 
                    src={type.image} 
                    alt={type.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* Dark overlay for better text readability */}
                  <div className="absolute inset-0 bg-black/40" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <type.icon className="h-5 w-5 text-secondary" />
                      <h3 className="font-heading text-xl font-semibold text-white drop-shadow-lg">{type.title}</h3>
                    </div>
                    <p className="text-white/90 text-sm mb-4 drop-shadow-md">{type.description}</p>
                    <Button 
                      onClick={() => navigate(type.link)}
                      className="bg-secondary hover:bg-secondary/90 text-white rounded-full"
                      data-testid={`travel-type-${type.title.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      {type.cta}
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="how-it-works" className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-secondary font-semibold mb-2">Just The Way You Want</p>
            <h2 className="font-heading text-4xl font-bold text-primary mb-4">
              Hassle-free | Authentic | Connected
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-secondary" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-primary mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Trips Section */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-8">
            <div>
              <h2 className="font-heading text-4xl font-bold text-primary mb-2">Featured Itineraries</h2>
              <p className="text-muted-foreground">Our most popular adventures</p>
            </div>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/trips')}
              className="hidden sm:flex items-center gap-2"
              data-testid="view-all-trips-btn"
            >
              View All Trips
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {trips.slice(0, 3).map((trip, idx) => (
              <motion.div
                key={trip.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <TripCard trip={trip} />
              </motion.div>
            ))}
          </div>

          <div className="mt-8 text-center sm:hidden">
            <Button onClick={() => navigate('/trips')} variant="outline" data-testid="view-all-trips-mobile-btn">
              View All Trips
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Only show if there are testimonials */}
      {testimonials.length > 0 && (
        <section id="testimonials" className="py-16 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="font-heading text-4xl font-bold mb-4">Hear From Our Happy Travelers</h2>
            </div>

            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12">
                <div className="flex justify-center mb-6">
                  {[...Array(testimonials[currentTestimonial]?.rating || 5)].map((_, i) => (
                    <Star key={i} className="h-6 w-6 fill-secondary text-secondary" />
                  ))}
                </div>
                <blockquote className="text-lg md:text-xl text-center mb-8 italic">
                  "{testimonials[currentTestimonial]?.text}"
                </blockquote>
                <div className="flex items-center justify-center gap-4">
                  {testimonials[currentTestimonial]?.avatar_url && (
                    <img 
                      src={testimonials[currentTestimonial].avatar_url} 
                      alt={testimonials[currentTestimonial].name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  )}
                  <div className="text-left">
                    <p className="font-semibold">{testimonials[currentTestimonial]?.name}</p>
                    <p className="text-sm text-primary-foreground/70">{testimonials[currentTestimonial]?.trip_title}</p>
                  </div>
                </div>
              </div>

              {testimonials.length > 1 && (
                <div className="flex justify-center gap-4 mt-8">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={prevTestimonial}
                    className="rounded-full border-white/30 text-white hover:bg-white/10"
                    data-testid="testimonial-prev-btn"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <div className="flex items-center gap-2">
                    {testimonials.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentTestimonial(idx)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          idx === currentTestimonial ? 'bg-secondary w-6' : 'bg-white/30'
                        }`}
                      />
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={nextTestimonial}
                    className="rounded-full border-white/30 text-white hover:bg-white/10"
                    data-testid="testimonial-next-btn"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-secondary/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl font-bold text-primary mb-4">
            Ready to Start Your Adventure?
          </h2>
          <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
            Book now and pay over time with our flexible installment plans. Your dream trip is more affordable than ever.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/trips')}
              className="bg-secondary hover:bg-secondary/90 text-white rounded-full px-8"
              data-testid="cta-book-adventure-btn"
            >
              Book Your Adventure Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          {/* Newsletter */}
          <div className="mt-10">
            <p className="text-sm text-muted-foreground mb-4">Get exclusive access to booking and updates</p>
            <form onSubmit={handleSubscribe} className="flex gap-2 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
                data-testid="newsletter-email-input"
              />
              <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="newsletter-subscribe-btn">
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
